import {
  Component,
  HostListener,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatTooltip } from '@angular/material/tooltip';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/input';
import { MatInput } from '@angular/material/input';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CompanyFilter } from '../../../shared/model/company.model';
import { CompanyService } from '../../../services/company.service';
import moment, { Moment } from 'moment';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { CertificateOverviewService } from '../../../services/certificate-overview.service';
import { CertificateTemplateService } from '../../../services/certificate-template.service';
import {
  CertificateReminderType,
  ICertificateMilestone,
  ICertificateOverview,
  ICertificateReminderRequest,
  MilestoneStatus,
} from '../../../shared/model/certificate-overview.model';
import { IUserCertificateTemplate } from '../../../shared/model/user-certificate-template.model';
import { MessageService } from '../../../shared/components/message.service';
import { UserRoleService } from '../../../services/userRole.service';
import {
  CertificateTemplate,
  PREDEFINED_TEMPLATES,
} from './certificate-templates.constants';

export interface CertificateColumn {
  key: string;
  certKey: string;
  label: string;
  fullName: string;
}

export const CERTIFICATE_COLUMNS: CertificateColumn[] = [
  {
    key: 'ctfl',
    certKey: 'CTFL',
    label: 'CTFL',
    fullName: 'Certified Tester Foundation Level (CTFL)',
  },
  {
    key: 'ctAi',
    certKey: 'CT-AI',
    label: 'CT-AI',
    fullName: 'Certified Tester AI Testing (CT-AI)',
  },
  {
    key: 'ctGenAi',
    certKey: 'CT-GenAI',
    label: 'CT-GenAI',
    fullName: 'Certified Tester - Testing with Generative AI (CT-GenAI)',
  },
  {
    key: 'ctalTm',
    certKey: 'CTAL-TM',
    label: 'CTAL-TM',
    fullName: 'Certified Tester Advanced Level Test Management (CTAL-TM)',
  },
  {
    key: 'ctalTa',
    certKey: 'CTAL-TA',
    label: 'CTAL-TA',
    fullName: 'Certified Tester Advanced Level Test Analysis (CTAL-TA)',
  },
  {
    key: 'ctalTae',
    certKey: 'CTAL-TAE',
    label: 'CTAL-TAE',
    fullName:
      'Certified Tester Advanced Level Test Automation Engineering (CTAL-TAE)',
  },
  {
    key: 'cprefl',
    certKey: 'CPRE-FL',
    label: 'CPRE-FL',
    fullName:
      'Certified Professional for Requirements Engineering - Foundation Level (CPRE-FL)',
  },
  {
    key: 'ctflAt',
    certKey: 'CTFL-AT',
    label: 'CTFL-AT',
    fullName: 'Certified Tester Foundation Level Agile Tester (CTFL-AT)',
  },
  {
    key: 'ctFt',
    certKey: 'CT-FT',
    label: 'CT-FT',
    fullName: 'Certified Tester Foundation Level - Flexible Testing (CT-FT)',
  },
  {
    key: 'a4qAiEssentials',
    certKey: 'A4Q AI Essentials',
    label: 'A4Q AI Essentials',
    fullName: 'A4Q AI Essentials',
  },
  {
    key: 'ctAct',
    certKey: 'CT-AcT',
    label: 'CT-AcT',
    fullName: 'Certified Tester - Acceptance Testing (CT-AcT)',
  },
  {
    key: 'ctPt',
    certKey: 'CT-PT',
    label: 'CT-PT',
    fullName: 'Certified Tester - Performance Testing (CT-PT)',
  },
  {
    key: 'ctMat',
    certKey: 'CT-MAT',
    label: 'CT-MAT',
    fullName: 'Certified Tester - Mobile Application Testing (CT-MAT)',
  },
  {
    key: 'ctMbt',
    certKey: 'CT-MBT',
    label: 'CT-MBT',
    fullName: 'Certified Tester - Model-Based Testing (CT-MBT)',
  },
  {
    key: 'ctSec',
    certKey: 'CT-SEC',
    label: 'CT-SEC',
    fullName: 'Certified Tester - Security Testing (CT-SEC)',
  },
  {
    key: 'ctal',
    certKey: 'CTAL',
    label: 'CTAL',
    fullName: 'Certified Tester Advanced Level (CTAL)',
  },
  {
    key: 'ctalTta',
    certKey: 'CTAL-TTA',
    label: 'CTAL-TTA',
    fullName:
      'Certified Tester Advanced Level Technical Test Analyst (CTAL-TTA)',
  },
  {
    key: 'ctflAut',
    certKey: 'CTFL-AuT',
    label: 'CTFL-AuT',
    fullName:
      'Certified Tester Foundation Level Automotive Software Tester (CTFL-AuT)',
  },
];

const MILESTONE_ICONS: Record<MilestoneStatus, string> = {
  certified: 'check_circle',
  exam_planned: 'event',
  training_done: 'school',
  training_planned: 'schedule',
  none: '',
};

// Sort rank for certificate columns: certified > exam_planned > training_done >
// training_planned. 'none' is 0 (falsy) so it can be pinned to the bottom regardless
// of sort direction. (NP20-743)
const MILESTONE_SORT_RANK: Record<MilestoneStatus, number> = {
  certified: 1,
  exam_planned: 2,
  training_done: 3,
  training_planned: 4,
  none: 0,
};

type MilestoneEditState = {
  trainingPlanned: Moment | null;
  trainingDate: Moment | null;
  examDate: Moment | null;
  certifiedDate: Moment | null;
};

@Component({
  selector: 'app-certificate-overview',
  standalone: true,
  imports: [
    FormsModule,
    NgClass,
    MatIcon,
    MatProgressSpinner,
    MatButton,
    MatIconButton,
    MatTable,
    MatSort,
    MatSortHeader,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatSelect,
    MatOption,
    MatRadioGroup,
    MatRadioButton,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    MatCard,
    MatCardContent,
    MatCheckbox,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    TranslatePipe,
    RouterLink,
    MatTooltip,
    ModalComponent,
  ],
  templateUrl: './certificate-overview.component.html',
  styleUrl: './certificate-overview.component.scss',
})
export class CertificateOverviewComponent implements OnInit {
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  dataSource = new MatTableDataSource<ICertificateOverview>();
  loading = false;
  searchTerm = '';
  selectedCompany: CompanyFilter = 'ALL';
  applicantOnly = false;
  freelancerOnly = false;
  private readonly companyService = inject(CompanyService);
  get companies() {
    return this.companyService.companies;
  }

  readonly CERTIFICATE_COLUMNS = CERTIFICATE_COLUMNS;
  readonly PREDEFINED_TEMPLATES = PREDEFINED_TEMPLATES;
  readonly MILESTONE_ICONS = MILESTONE_ICONS;

  displayedColumns: string[] = [];
  isAdminOrOffice = false;

  mailModalVisible = false;
  selectedConsultant: ICertificateOverview | null = null;
  selectedCertificates: string[] = [];
  reminderType: CertificateReminderType = 'reminder';
  mailText = '';
  sending = false;

  editModalVisible = false;
  editConsultant: ICertificateOverview | null = null;
  editMilestones: Record<string, MilestoneEditState> = {};
  savingMilestones = false;

  userTemplates: IUserCertificateTemplate[] = [];
  activeTemplateId = 'standard';
  templateDropdownOpen = false;
  tplMenuTopPx = 0;
  tplMenuLeftPx = 0;
  templatePickerVisible = false;
  customCertKeys: string[] = [];
  certPickerSearchTerm = '';
  customTemplateName = '';
  showSaveNameInput = false;
  savingTemplate = false;

  deleteConfirmVisible = false;
  deleteConfirmId: string | null = null;
  deleteConfirmName = '';

  expandedRows = new Set<string>();
  tooltipData: {
    row: ICertificateOverview;
    cert: CertificateColumn;
    milestone: ICertificateMilestone;
    x: number;
    y: number;
  } | null = null;

  private readonly certKeyByColtKey = new Map<string, string>(
    CERTIFICATE_COLUMNS.map((c) => [c.key, c.key]),
  );

  constructor(
    private certificateOverviewService: CertificateOverviewService,
    private certificateTemplateService: CertificateTemplateService,
    private userRoleService: UserRoleService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.dataSource.filterPredicate = (row, filter) => {
      const { search, firm, applicant, freelancer } = JSON.parse(filter) as {
        search: string;
        firm: CompanyFilter;
        applicant: boolean;
        freelancer: boolean;
      };
      const matchesSearch =
        !search || row.consultantName.toLowerCase().includes(search);
      const matchesFirm = firm === 'ALL' || row.firma === firm;
      const matchesApplicant = !applicant || row.applicant === true;
      const matchesFreelancer = !freelancer || row.freelancer === true;
      return matchesSearch && matchesFirm && matchesApplicant && matchesFreelancer;
    };

    this.dataSource.sortData = (data, sort) => this.sortRows(data, sort);
  }

  private sortRows(
    data: ICertificateOverview[],
    sort: MatSort,
  ): ICertificateOverview[] {
    const active = sort.active;
    const direction = sort.direction;
    if (!active || direction === '') {
      return data;
    }

    const byName = (a: ICertificateOverview, b: ICertificateOverview) =>
      a.consultantName.localeCompare(b.consultantName);
    const factor = direction === 'asc' ? 1 : -1;

    if (active === 'consultantName') {
      return [...data].sort((a, b) => factor * byName(a, b));
    }

    // Certificate column: sort by milestone status rank; rows without a status
    // ('none'/empty) are pinned to the bottom in BOTH directions, so a plain numeric
    // sortingDataAccessor (which MatSort just reverses) is not enough. (NP20-743)
    return [...data].sort((a, b) => {
      const ra = MILESTONE_SORT_RANK[a.milestones?.[active]?.status ?? 'none'];
      const rb = MILESTONE_SORT_RANK[b.milestones?.[active]?.status ?? 'none'];
      if (!ra && !rb) return byName(a, b);
      if (!ra) return 1;
      if (!rb) return -1;
      return ra !== rb ? factor * (ra - rb) : byName(a, b);
    });
  }

  ngOnInit(): void {
    this.isAdminOrOffice = this.userRoleService.includesRoles([
      'Admin',
      'Office',
    ]);
    this.applyTemplateById('standard');
    this.companyService.load().subscribe();
    this.restoreFilters();

    this.loading = true;
    this.certificateOverviewService.getOverview().subscribe({
      next: (data) => {
        this.dataSource.data = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.error('certificateOverview.loadError');
      },
    });

    this.certificateTemplateService.loadTemplates().subscribe({
      next: (templates) => (this.userTemplates = templates),
      error: () => {},
    });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.templateDropdownOpen = false;
  }

  @HostListener('document:scroll')
  @HostListener('window:resize')
  onScrollOrResize(): void {
    if (this.templateDropdownOpen) {
      this.templateDropdownOpen = false;
    }
  }

  toggleTemplateDropdown(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.templateDropdownOpen) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.tplMenuTopPx = rect.bottom + 6;
      this.tplMenuLeftPx = rect.left;
    }
    this.templateDropdownOpen = !this.templateDropdownOpen;
  }

  confirmDeleteTemplate(id: string, name: string): void {
    this.deleteConfirmId = id;
    this.deleteConfirmName = name;
    this.deleteConfirmVisible = true;
    this.templateDropdownOpen = false;
  }

  executeDeleteTemplate(): void {
    if (this.deleteConfirmId) {
      this.deleteUserTemplate(this.deleteConfirmId);
    }
    this.deleteConfirmVisible = false;
    this.deleteConfirmId = null;
  }

  applyFilter(): void {
    this.dataSource.filter = JSON.stringify({
      search: this.searchTerm.trim().toLowerCase(),
      firm: this.selectedCompany,
      applicant: this.applicantOnly,
      freelancer: this.freelancerOnly,
    });
  }

  chipStyle(color: string, active: boolean): Record<string, string> {
    if (!active) {
      return {};
    }
    return {
      background: `color-mix(in srgb, ${color} 12%, white)`,
      color,
      'border-color': `color-mix(in srgb, ${color} 45%, white)`,
    };
  }

  onCompanyFilterChange(value: CompanyFilter): void {
    if (this.selectedCompany === value) return;
    this.selectedCompany = value;
    // Persist in the URL so the filter survives reload/navigation, like the Beraterübersicht.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { firma: value === 'ALL' ? null : value },
      queryParamsHandling: 'merge',
    });
    this.applyFilter();
  }

  // Wie in der Beraterübersicht: `true` in der URL, beim Ausschalten fällt der Parameter weg.
  onApplicantFilterToggle(): void {
    this.applicantOnly = !this.applicantOnly;
    this.persistFlag('applicant', this.applicantOnly);
  }

  onFreelancerFilterToggle(): void {
    this.freelancerOnly = !this.freelancerOnly;
    this.persistFlag('freelancer', this.freelancerOnly);
  }

  private persistFlag(param: 'applicant' | 'freelancer', on: boolean): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [param]: on ? true : null },
      queryParamsHandling: 'merge',
    });
    this.applyFilter();
  }

  private restoreFilters(): void {
    const params = this.route.snapshot.queryParams;
    const firma = params['firma'];
    if (typeof firma === 'string' && firma.length > 0) {
      this.selectedCompany = firma;
    }
    this.applicantOnly = params['applicant'] === 'true';
    this.freelancerOnly = params['freelancer'] === 'true';
    this.applyFilter();
  }

  toggleExpand(row: ICertificateOverview): void {
    const id = row.consultantId;
    this.expandedRows.has(id)
      ? this.expandedRows.delete(id)
      : this.expandedRows.add(id);
  }

  isExpanded(row: ICertificateOverview): boolean {
    return this.expandedRows.has(row.consultantId);
  }

  getActiveCertsForRow(
    row: ICertificateOverview,
  ): Array<{ col: CertificateColumn; milestone: ICertificateMilestone }> {
    return CERTIFICATE_COLUMNS.filter(
      (col) =>
        row.milestones?.[col.key]?.status &&
        row.milestones[col.key].status !== 'none',
    ).map((col) => ({ col, milestone: row.milestones[col.key] }));
  }

  onCellMouseEnter(
    event: MouseEvent,
    row: ICertificateOverview,
    certKey: string,
  ): void {
    const cert = CERTIFICATE_COLUMNS.find((c) => c.key === certKey);
    const milestone = row.milestones?.[certKey];
    if (!cert || !milestone || milestone.status === 'none') return;
    this.tooltipData = {
      row,
      cert,
      milestone,
      x: event.clientX,
      y: event.clientY,
    };
  }

  onCellMouseMove(event: MouseEvent): void {
    if (this.tooltipData) {
      this.tooltipData.x = event.clientX;
      this.tooltipData.y = event.clientY;
    }
  }

  onCellMouseLeave(): void {
    this.tooltipData = null;
  }

  milestoneFields(
    milestone: ICertificateMilestone,
  ): Array<{ icon: string; labelKey: string; value: string }> {
    const f: Array<{ icon: string; labelKey: string; value: string }> = [];
    if (milestone.trainingPlanned)
      f.push({
        icon: 'schedule',
        labelKey: 'certificateOverview.milestoneEditor.trainingPlanned',
        value: this.formatDate(milestone.trainingPlanned),
      });
    if (milestone.trainingDate)
      f.push({
        icon: 'school',
        labelKey: 'certificateOverview.milestoneEditor.trainingDate',
        value: this.formatDate(milestone.trainingDate),
      });
    if (milestone.examDate)
      f.push({
        icon: 'event',
        labelKey: 'certificateOverview.milestoneEditor.examDate',
        value: this.formatDate(milestone.examDate),
      });
    if (milestone.certifiedDate)
      f.push({
        icon: 'check_circle',
        labelKey: 'certificateOverview.milestoneEditor.certifiedDate',
        value: this.formatDate(milestone.certifiedDate),
      });
    return f;
  }

  getMilestone(
    row: ICertificateOverview,
    colKey: string,
  ): ICertificateMilestone | null {
    const certKey = this.certKeyByColtKey.get(colKey);
    return certKey ? (row.milestones?.[certKey] ?? null) : null;
  }

  milestoneDisplayDate(milestone: ICertificateMilestone): string {
    switch (milestone.status) {
      case 'certified':
        return this.formatDate(milestone.certifiedDate);
      case 'exam_planned':
        return this.formatDate(milestone.examDate);
      case 'training_done':
        return this.formatDate(milestone.trainingDate);
      case 'training_planned':
        return this.formatDate(milestone.trainingPlanned);
      default:
        return '';
    }
  }

  private formatDate(isoDate: string | null | undefined): string {
    if (!isoDate) return '';
    const p = isoDate.split('-');
    return p.length === 3 ? `${p[2]}.${p[1]}.${p[0].slice(2)}` : isoDate;
  }

  get editActiveCertCount(): number {
    return CERTIFICATE_COLUMNS.filter(
      (col) =>
        this.editMilestones[col.key] &&
        this.deriveStatus(this.editMilestones[col.key]) !== 'none',
    ).length;
  }

  get tableMinWidth(): string {
    const certCols = this.displayedColumns.filter(
      (c) => c !== 'consultantName' && c !== 'actions',
    ).length;
    return `calc(10.625rem + ${certCols} * 4.5rem + 8.25rem)`;
  }

  get detailRowMinWidth(): string {
    return this.tableMinWidth;
  }

  getActiveTemplateName(): string {
    if (this.activeTemplateId === 'custom') {
      return this.translateService.instant(
        'certificateOverview.templates.custom',
      );
    }
    const t = this.getTemplate(this.activeTemplateId);
    if ('nameKey' in t) {
      return this.translateService.instant(t.nameKey);
    }
    return t.name;
  }

  applyTemplateById(id: string): void {
    const template = this.getTemplate(id);
    this.activeTemplateId = id;
    this.displayedColumns = ['consultantName', ...template.certs, 'actions'];
  }

  get filteredCertColumnsForPicker(): CertificateColumn[] {
    if (!this.certPickerSearchTerm.trim()) return CERTIFICATE_COLUMNS;
    const term = this.certPickerSearchTerm.toLowerCase();
    return CERTIFICATE_COLUMNS.filter(
      (c) =>
        c.label.toLowerCase().includes(term) ||
        c.fullName.toLowerCase().includes(term),
    );
  }

  openTemplatePicker(): void {
    const current = this.getTemplate(this.activeTemplateId);
    this.customCertKeys = [...current.certs];
    this.certPickerSearchTerm = '';
    this.customTemplateName = '';
    this.showSaveNameInput = false;
    this.templatePickerVisible = true;
  }

  applyPresetToCustom(certKeys: string[]): void {
    this.customCertKeys = [...certKeys];
  }

  applyUserTemplateInPicker(t: IUserCertificateTemplate): void {
    this.customCertKeys = [...t.certs];
  }

  toggleCustomCert(colKey: string, checked: boolean): void {
    if (checked) {
      if (!this.customCertKeys.includes(colKey))
        this.customCertKeys = [...this.customCertKeys, colKey];
    } else {
      this.customCertKeys = this.customCertKeys.filter((k) => k !== colKey);
    }
  }

  applyCustomTemplate(): void {
    if (this.customCertKeys.length === 0) return;
    this.activeTemplateId = 'custom';
    this.displayedColumns = [
      'consultantName',
      ...this.customCertKeys,
      'actions',
    ];
    this.templatePickerVisible = false;
  }

  saveCustomTemplate(): void {
    if (!this.customTemplateName.trim() || this.customCertKeys.length === 0)
      return;
    this.savingTemplate = true;
    this.certificateTemplateService
      .saveTemplate(this.customTemplateName.trim(), this.customCertKeys)
      .subscribe({
        next: (saved) => {
          this.userTemplates = [...this.userTemplates, saved];
          this.activeTemplateId = saved.id;
          this.displayedColumns = ['consultantName', ...saved.certs, 'actions'];
          this.savingTemplate = false;
          this.showSaveNameInput = false;
          this.templatePickerVisible = false;
          this.messageService.success(
            'certificateOverview.templatePicker.saveSuccess',
          );
        },
        error: () => {
          this.messageService.error(
            'certificateOverview.templatePicker.saveError',
          );
          this.savingTemplate = false;
        },
      });
  }

  deleteUserTemplate(id: string): void {
    this.certificateTemplateService.deleteTemplate(id).subscribe({
      next: () => {
        this.userTemplates = this.userTemplates.filter((t) => t.id !== id);
        if (this.activeTemplateId === id) {
          this.applyTemplateById('standard');
        }
        this.messageService.success(
          'certificateOverview.templatePicker.deleteSuccess',
        );
      },
      error: () =>
        this.messageService.error(
          'certificateOverview.templatePicker.deleteError',
        ),
    });
  }

  private getTemplate(
    id: string,
  ): CertificateTemplate | IUserCertificateTemplate {
    return (
      PREDEFINED_TEMPLATES.find((t) => t.id === id) ??
      this.userTemplates.find((t) => t.id === id) ??
      PREDEFINED_TEMPLATES[0]
    );
  }

  openEditModal(consultant: ICertificateOverview): void {
    this.editConsultant = consultant;
    this.editMilestones = {};
    for (const col of CERTIFICATE_COLUMNS) {
      const m = consultant.milestones?.[col.key];
      this.editMilestones[col.key] = {
        trainingPlanned: m?.trainingPlanned ? moment(m.trainingPlanned) : null,
        trainingDate: m?.trainingDate ? moment(m.trainingDate) : null,
        examDate: m?.examDate ? moment(m.examDate) : null,
        certifiedDate: m?.certifiedDate ? moment(m.certifiedDate) : null,
      };
    }
    this.savingMilestones = false;
    this.editModalVisible = true;
  }

  deriveStatus(state: MilestoneEditState): MilestoneStatus {
    if (state.certifiedDate?.isValid()) return 'certified';
    if (state.examDate?.isValid()) return 'exam_planned';
    if (state.trainingDate?.isValid()) return 'training_done';
    if (state.trainingPlanned?.isValid()) return 'training_planned';
    return 'none';
  }

  saveMilestones(): void {
    if (!this.editConsultant) return;
    const toIso = (v: Moment | null) =>
      v?.isValid() ? v.format('YYYY-MM-DD') : null;
    const payload: Record<
      string,
      {
        trainingPlanned: string | null;
        trainingDate: string | null;
        examDate: string | null;
        certifiedDate: string | null;
      }
    > = {};
    for (const [certKey, state] of Object.entries(this.editMilestones)) {
      payload[certKey] = {
        trainingPlanned: toIso(state.trainingPlanned),
        trainingDate: toIso(state.trainingDate),
        examDate: toIso(state.examDate),
        certifiedDate: toIso(state.certifiedDate),
      };
    }
    this.savingMilestones = true;
    this.certificateOverviewService
      .updateMilestones(this.editConsultant.consultantId, payload)
      .subscribe({
        next: () => {
          const idx = this.dataSource.data.findIndex(
            (r) => r.consultantId === this.editConsultant!.consultantId,
          );
          if (idx >= 0) {
            const updated: ICertificateOverview = {
              ...this.dataSource.data[idx],
              milestones: { ...this.dataSource.data[idx].milestones },
            };
            for (const [certKey, state] of Object.entries(
              this.editMilestones,
            )) {
              updated.milestones[certKey] = {
                trainingPlanned: toIso(state.trainingPlanned),
                trainingDate: toIso(state.trainingDate),
                examDate: toIso(state.examDate),
                certifiedDate: toIso(state.certifiedDate),
                status: this.deriveStatus(state),
              };
            }
            const data = [...this.dataSource.data];
            data[idx] = updated;
            this.dataSource.data = data;
          }
          this.messageService.success(
            'certificateOverview.milestoneEditor.success',
          );
          this.editModalVisible = false;
          this.savingMilestones = false;
        },
        error: () => {
          this.messageService.error(
            'certificateOverview.milestoneEditor.error',
          );
          this.savingMilestones = false;
        },
      });
  }

  openMailModal(consultant: ICertificateOverview): void {
    this.selectedConsultant = consultant;
    this.selectedCertificates = [];
    this.reminderType = 'reminder';
    this.sending = false;
    this.updateMailText();
    this.mailModalVisible = true;
  }

  onCertificatesChange(): void {
    this.updateMailText();
  }

  onTypeChange(): void {
    this.updateMailText();
  }

  updateMailText(): void {
    const firstName =
      this.selectedConsultant?.consultantFirstName ??
      this.selectedConsultant?.consultantName ??
      '';
    const certList = this.selectedCertificates.join(', ') || '[Zertifikat]';
    const key =
      this.reminderType === 'reminder'
        ? 'certificateOverview.mailModal.reminderTemplate'
        : 'certificateOverview.mailModal.newCertTemplate';
    this.mailText = this.translateService.instant(key, {
      firstName,
      certs: certList,
    });
  }

  sendReminder(): void {
    if (!this.selectedConsultant || this.selectedCertificates.length === 0)
      return;
    const request: ICertificateReminderRequest = {
      consultantId: this.selectedConsultant.consultantId,
      certificates: this.selectedCertificates,
      type: this.reminderType,
      message: this.mailText,
    };
    this.sending = true;
    this.certificateOverviewService.sendReminder(request).subscribe({
      next: () => {
        this.messageService.success('certificateOverview.mailModal.success');
        this.mailModalVisible = false;
        this.sending = false;
      },
      error: () => {
        this.messageService.error('certificateOverview.mailModal.error');
        this.sending = false;
      },
    });
  }
}
